import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import EzTable from "../../../common/EzTable";
import { EzToast, TOAST_SEVERITY } from "../../../common/EzToast";
import EzNotFound from "../../../common/EzNotFound";
import { Box, Container, Grid } from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { Button } from "primereact/button";
import { useStyles } from "./style";
import EzControlledCalendarWithFloatingLabel from "../../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import { getFormBaseValidationRule } from "../../../utils/form/form-utils";
import EzControlledDropDowntWithFloatingLabel from "../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import EzControlledMultiSelecttWithFloatingLabel from "../../../common/EzForm/EzControlledMultiSelect/WithFloatingLabel";
import { ReservationsReservationsBreadcrumb, bookingTypeDD } from "../ReservationsReservations/CreateReservationsReservations/constant";
import { getCurrentAndThreeDaysLater } from "../../../utils";
import { printTableData } from "../../../utils/printUtils";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;

const ReservationsByLocationAndNetwork = (props) => {
  const classes = useStyles();
  const {
    fetchReservationsByLocationAndNetwork,
    tableData,
    exportReservationsByLocationAndNetwork,
    isPageLevelError,
    paginationInfo,
    isLoading,
    RateNetworkDD,
    locationDD,
    filters: savedFilters,
    setFilters,
    clearFilters,
  } = props;

  const { currentDate, threeDaysLater } = getCurrentAndThreeDaysLater();
  const customFields = {
    endDate: savedFilters?.endDate || threeDaysLater,
    startDate: savedFilters?.startDate || currentDate,
    location: savedFilters?.location || "",
    network: savedFilters?.network || "",
    type: savedFilters?.type || "",
  };

  const {
    control,
    formState: { errors },
    register,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: useMemo(() => customFields, [savedFilters]),
  });

  const [isCopy, setIsCopy] = useState(false);
  const [isPrintError, setIsPrintError] = useState(false);

  useEffect(() => {
    if (savedFilters) {
      const { currentDate, threeDaysLater } = getCurrentAndThreeDaysLater();
      reset({
        endDate: savedFilters?.endDate || threeDaysLater,
        startDate: savedFilters?.startDate || currentDate,
        location: savedFilters?.location || "",
        network: savedFilters?.network || "",
        type: savedFilters?.type || "",
      });
    }
  }, [savedFilters, reset]);

  const convertToTSV = (data) => {
    const headers = data.columns.map((item) => item.colLabel).join("\t");
    const rows = data.rows
      .map((row) =>
        data.columns
          .map((col) => {
            const value = row[col.dataKey];
            return value === null || value === undefined ? "-" : String(value);
          })
          .join("\t")
      )
      .join("\n");
    return `${headers}\n${rows}`;
  };

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const copyToClipboard = () => {
    const csv = convertToTSV(tableData);
    navigator.clipboard
      .writeText(csv)
      .then(() => setIsCopy(true))
      .catch(() => setIsCopy(true));
  };

  useEffect(() => {
    setTimeout(() => setIsCopy(false), 1000);
  }, [isCopy]);

  useEffect(() => {
    if (isPrintError) {
      const timer = setTimeout(() => setIsPrintError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isPrintError]);

  const handlePrint = () => {
    const success = printTableData({
      title: "Reservations by Location & Network Report",
      columns: tableData?.columns || [],
      rows: tableData?.rows || [],
      orientation: "landscape",
    });
    if (!success) {
      setIsPrintError(true);
    }
  };

  const handleExport = () => {
    exportReservationsByLocationAndNetwork({
      additinalFilter: savedFilters,
    });
  };

  const onSubmit = (data) => {
    setFilters(data);
    fetchReservationsByLocationAndNetwork({
      pageNumber: 0,
      pageSize: paginationInfo?.pageSize || 10,
      additinalFilter: data,
    });
  };

  const handleResetFilters = () => {
    clearFilters();
    const { currentDate, threeDaysLater } = getCurrentAndThreeDaysLater();
    fetchReservationsByLocationAndNetwork({
      pageNumber: 0,
      pageSize: paginationInfo?.pageSize || 10,
      additinalFilter: {
        startDate: currentDate,
        endDate: threeDaysLater,
        location: "",
        network: "",
        type: "",
      },
    });
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Reservations by Location & Network</h4>
          </div>
          <div className="buttonarea">
            <div className="copy-excel-print-group">
              <Button
                label="Copy"
                className="p-button-raised"
                style={{ borderRadius: "0px" }}
                onClick={copyToClipboard}
              />
              <Button
                label="Excel"
                className="p-button-raised"
                style={{ borderRadius: "0px" }}
                onClick={handleExport}
              />
              <Button
                label="Print"
                className="p-button-raised"
                style={{ borderRadius: "0px" }}
                onClick={handlePrint}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderRecords = () => tableData?.columns?.length > 0;
  const shouldRenderNotFoundView = () =>
    !shouldRenderRecords() && !shouldRenderFullPageError();

  const filters_obj = {
    global: { value: null },
  };

  const getToastProps = () => {
    if (isPrintError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle:
          "Unable to open print window. Please check your popup blocker settings.",
        shouldShowToast: true,
      };
    }
    if (isCopy) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Data copied to clipboard!",
        shouldShowToast: true,
      };
    }
  };

  const tableProps = {
    columns: tableData?.columns,
    paginationInfo,
    value: tableData?.rows,
    emptyMessage: "No data found. Please search with filters.",
    filters: filters_obj,
    loadLazyData: fetchReservationsByLocationAndNetwork,
    loading: isLoading,
    showMoreActions: false,
    sortField: null,
    additinalFilter: savedFilters,
    showViewButton: false,
    showEditButton: false,
    showDeleteButton: false,
    setDeleteConfirmationPopupVisibility: () => {},
  };

  return (
    <>
      <Container maxWidth="lg">
        <Breadcrumb list={ReservationsReservationsBreadcrumb} />
        <EzToast {...getToastProps()} />
        <div className={`containerbox ${classes.bg}`}>
          {renderPageHeaderSection()}
          <div className="form-demo">
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledCalendarWithFloatingLabel
                    id="startDate"
                    name="startDate"
                    {...register("startDate")}
                    showTime={false}
                    maxDate={new Date(2090, 0, 1)}
                    dateFormat="yy/mm/dd"
                    control={control}
                    isError={errors["startDate"]}
                    errorMsg={getFormErrorMessage("startDate")}
                    label="Start Date"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledCalendarWithFloatingLabel
                    id="endDate"
                    name="endDate"
                    {...register("endDate")}
                    showTime={false}
                    maxDate={new Date(2090, 0, 1)}
                    dateFormat="yy/mm/dd"
                    control={control}
                    isError={errors["endDate"]}
                    errorMsg={getFormErrorMessage("endDate")}
                    label="End Date"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledMultiSelecttWithFloatingLabel
                    name="location"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                    optionLabel="name"
                    optionValue="id"
                    options={locationDD}
                    isError={errors["location"]}
                    errorMsg={getFormErrorMessage("location")}
                    label="Location"
                    labelClassName="ml-2"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledMultiSelecttWithFloatingLabel
                    name="network"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                    optionLabel="name"
                    optionValue="id"
                    options={RateNetworkDD}
                    isError={errors["network"]}
                    errorMsg={getFormErrorMessage("network")}
                    label="Network"
                    labelClassName="ml-2"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="type"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                    optionLabel="name"
                    optionValue="id"
                    options={bookingTypeDD}
                    isError={errors["type"]}
                    errorMsg={getFormErrorMessage("type")}
                    label="Type"
                    labelClassName="ml-2"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <div
                    style={{
                      margin: "0px 0px 10px 10px",
                      maxWidth: "200px",
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <Button
                      type="button"
                      label="Search"
                      className="p-button-raised"
                      onClick={handleSubmit(onSubmit)}
                    />
                    <Button
                      type="button"
                      label="Reset"
                      className="p-button-raised p-button-secondary"
                      onClick={handleResetFilters}
                    />
                  </div>
                </Grid>
              </Grid>
            </form>
          </div>
          <div className="mTop15">
            {shouldRenderFullPageError() && (
              <EzNotFound
                messageStyle={{ marginLeft: "-70px" }}
                bgImgUrl={getIconPath("something-broke.png")}
                message="Oops! Something broke, we are working on a fix."
              />
            )}
            {shouldRenderRecords() && (
              <Box id="printable">
                <EzTable {...tableProps} />
              </Box>
            )}
            {shouldRenderNotFoundView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message="No records found. Please use the filters above to search."
              />
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default ReservationsByLocationAndNetwork;
